/**
 * Compact per-tick economy/compliance/threat breakdown — always visible during play.
 * Not gated by HUD preferences (core-loop feedback).
 */
import React from 'react';
import type { SimulationBreakdown } from '../../types';
import { surfaces, type as typeTokens, cn } from './kit';

export type SimBreakdownStripProps = {
  simBreakdown: SimulationBreakdown;
  onOpenFinance?: () => void;
  onOpenCompliance?: () => void;
  onOpenSecurity?: () => void;
};

function formatSigned(value: number, prefix = ''): string {
  if (value === 0) return '—';
  const sign = value > 0 ? '+' : '';
  const rounded =
    Math.abs(value) >= 10 ? Math.round(value) : Number(value.toFixed(1));
  return `${sign}${prefix}${rounded}`;
}

const SimBreakdownStrip: React.FC<SimBreakdownStripProps> = ({
  simBreakdown,
  onOpenFinance,
  onOpenCompliance,
  onOpenSecurity,
}) => {
  const cashNet = simBreakdown.income.total - simBreakdown.expenses.total;
  const compNet = simBreakdown.compliance.net;
  const threatNet = simBreakdown.threat.net;

  const chipClass = (positive: boolean | null) =>
    cn(
      'px-2 py-0.5 rounded font-bold tabular-nums transition-colors',
      positive === true && 'text-emerald-400',
      positive === false && 'text-red-400',
      positive === null && 'text-slate-400',
    );

  return (
    <div
      data-testid="sim-breakdown-strip"
      className={cn(
        surfaces.hud,
        'flex flex-wrap items-center gap-x-3 gap-y-0.5 px-2 py-1 text-[10px] md:text-xs shadow-none',
      )}
      role="status"
      aria-live="polite"
    >
      <span className={cn(typeTokens.eyebrow, 'text-slate-500 shrink-0')}>Per tick</span>

      <button
        type="button"
        className="pointer-events-auto flex items-center gap-1 hover:bg-white/5 rounded px-1 transition-colors"
        onClick={onOpenFinance}
        title="Open finance breakdown"
      >
        <span className="text-slate-400">💰</span>
        <span className={chipClass(cashNet > 0 ? true : cashNet < 0 ? false : null)}>
          {formatSigned(cashNet, '$')}
        </span>
      </button>

      <button
        type="button"
        className="pointer-events-auto flex items-center gap-1 hover:bg-white/5 rounded px-1 transition-colors"
        onClick={onOpenCompliance}
        title="Open compliance breakdown"
      >
        <span className="text-slate-400">📊</span>
        <span className={chipClass(compNet > 0 ? true : compNet < 0 ? false : null)}>
          {formatSigned(compNet, '')}%
        </span>
      </button>

      <button
        type="button"
        className="pointer-events-auto flex items-center gap-1 hover:bg-white/5 rounded px-1 transition-colors"
        onClick={onOpenSecurity}
        title="Open security breakdown"
      >
        <span className="text-slate-400">⚠</span>
        <span
          className={chipClass(
            threatNet < 0 ? true : threatNet > 0 ? false : null,
          )}
        >
          {formatSigned(threatNet)}
        </span>
      </button>
    </div>
  );
};

export default SimBreakdownStrip;